import { Component } from '@theme/component';

/**
 * @typedef {object} ContactFormRefs
 * @property {HTMLFormElement | undefined} contactForm
 * @property {HTMLTextAreaElement | undefined} comment
 * @property {HTMLElement | undefined} commentGuidance
 */

/**
 * @typedef {object} ContactNodes
 * @property {HTMLFormElement} form
 * @property {HTMLTextAreaElement} comment
 * @property {string} message
 */

/** Native Contact validation; owns no submission or value normalization.
 * @extends Component<ContactFormRefs>
 */
class ContactForm extends Component {
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {HTMLTextAreaElement | undefined} */
  #comment;
  /** @type {string | undefined} */
  #validityMessage;
  /** @type {{ control: HTMLTextAreaElement, previous: string | null } | undefined} */
  #invalidState;
  #interacted = false;
  #generation = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTask;
  /** @type {string | undefined} */
  #diagnostic;
  #observer = new MutationObserver(() => this.#synchronize());

  /** @returns {void} */
  connectedCallback() {
    super.connectedCallback();
    this.#stopListening();
    this.#listeners = new AbortController();
    const options = { capture: true, signal: this.#listeners.signal };
    for (const type of ['input', 'change', 'blur', 'invalid', 'reset', 'submit']) {
      this.addEventListener(type, this.#handleEvent, options);
    }
    window.addEventListener('pageshow', this.#restore, { signal: this.#listeners.signal });
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['ref', 'id', 'aria-describedby', 'data-contact-validation-ready'],
    });
    this.#synchronize();
  }

  /** @returns {void} */
  updatedCallback() {
    super.updatedCallback();
    this.#synchronize();
  }

  /** @returns {void} */
  disconnectedCallback() {
    this.#stopListening();
    this.#releaseOwnedState();
    this.#comment = undefined;
    this.#interacted = false;
    super.disconnectedCallback();
  }

  /** @returns {void} */
  #stopListening() {
    this.#listeners?.abort();
    this.#observer.disconnect();
    clearTimeout(this.#resetTask);
    this.#resetTask = undefined;
    this.#generation += 1;
  }

  /** Resolve live nodes rather than Component refs which may await a mutation callback.
   * @returns {ContactNodes | undefined}
   */
  #resolveNodes() {
    const form = this.querySelector('[ref="contactForm"]');
    const comment = this.querySelector('[ref="comment"]');
    const guidance = this.querySelector('[ref="commentGuidance"]');
    if (
      !(form instanceof HTMLFormElement) ||
      !(comment instanceof HTMLTextAreaElement) ||
      !(guidance instanceof HTMLElement) ||
      form.closest('contact-form-component') !== this ||
      comment.closest('contact-form-component') !== this ||
      guidance.closest('contact-form-component') !== this ||
      comment.form !== form ||
      !form.contains(guidance) ||
      !form.id ||
      guidance.id !== `${form.id}-comment-validation` ||
      !comment.getAttribute('aria-describedby')?.split(/\s+/).includes(guidance.id)
    ) {
      this.#unavailable('Missing or mismatched Contact form, Comment or guidance reference.');
      return;
    }
    const message = guidance.textContent?.trim() ?? '';
    if (!message || /translation missing|blocks\.contact_form\.comment_blank_error/i.test(message)) {
      this.#unavailable('Localized Comment guidance is blank or unresolved.');
      return;
    }
    return { form, comment, message };
  }

  /** @param {string} reason @returns {void} */
  #unavailable(reason) {
    this.#releaseOwnedState();
    this.#comment = undefined;
    this.#interacted = false;
    this.removeAttribute('data-contact-validation-ready');
    if (this.#diagnostic !== reason) {
      console.warn(`[contact-form] Enhancement unavailable: ${reason}`);
      this.#diagnostic = reason;
    }
  }

  /** @returns {void} */
  #releaseOwnedState() {
    if (this.#comment && this.#validityMessage && this.#comment.validationMessage === this.#validityMessage) {
      this.#comment.setCustomValidity('');
    }
    this.#validityMessage = undefined;
    this.#clearInvalidState();
  }

  /** Restore only the aria-invalid state this component changed.
   * @returns {void}
   */
  #clearInvalidState() {
    const owned = this.#invalidState;
    if (owned?.control.getAttribute('aria-invalid') === 'true') {
      if (owned.previous === null) owned.control.removeAttribute('aria-invalid');
      else owned.control.setAttribute('aria-invalid', owned.previous);
    }
    this.#invalidState = undefined;
  }

  /** @param {HTMLTextAreaElement} comment @returns {void} */
  #showInvalidState(comment) {
    if (comment.getAttribute('aria-invalid') === 'true') return;
    this.#invalidState = { control: comment, previous: comment.getAttribute('aria-invalid') };
    comment.setAttribute('aria-invalid', 'true');
  }

  /** @returns {ContactNodes | undefined} */
  #synchronize() {
    if (!this.isConnected) return;
    const nodes = this.#resolveNodes();
    if (!nodes) return;
    if (nodes.comment !== this.#comment) {
      this.#releaseOwnedState();
      this.#comment = nodes.comment;
      this.#interacted = false;
    }
    const blank = nodes.comment.value.trim().length === 0;
    if (blank) {
      if (nodes.comment.validationMessage !== nodes.message) nodes.comment.setCustomValidity(nodes.message);
      this.#validityMessage = nodes.message;
    } else if (this.#validityMessage) {
      if (nodes.comment.validationMessage === this.#validityMessage) nodes.comment.setCustomValidity('');
      this.#validityMessage = undefined;
    }
    if (blank && this.#interacted) this.#showInvalidState(nodes.comment);
    else this.#clearInvalidState();
    // Omitted from Liquid: defeats morph's markup-equal skip of updatedCallback.
    if (!this.hasAttribute('data-contact-validation-ready')) this.setAttribute('data-contact-validation-ready', '');
    this.#diagnostic = undefined;
    return nodes;
  }

  /** @returns {void} */
  #restore = () => {
    this.#synchronize();
  };

  /** @param {Event} event @returns {void} */
  #handleEvent = (event) => {
    const nodes = this.#resolveNodes();
    if (!nodes) return;
    if (event.type === 'reset') {
      if (event.target !== nodes.form) return;
      clearTimeout(this.#resetTask);
      const generation = this.#generation;
      const form = nodes.form;
      this.#resetTask = setTimeout(() => {
        this.#resetTask = undefined;
        if (event.defaultPrevented || !this.isConnected || generation !== this.#generation) return;
        const current = this.#resolveNodes();
        if (!current || current.form !== form) return;
        this.#interacted = false;
        this.#clearInvalidState();
        this.#synchronize();
      }, 0);
      return;
    }
    if (event.type === 'submit') {
      if (event.target !== nodes.form) return;
      const current = this.#synchronize();
      if (current && current.comment.value.trim().length === 0) {
        this.#interacted = true;
        this.#showInvalidState(current.comment);
        event.preventDefault();
        current.comment.reportValidity();
      }
      return;
    }
    if (event.target !== nodes.comment) return;
    if (event.type === 'blur' || event.type === 'invalid') this.#interacted = true;
    this.#synchronize();
  };
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactForm);
}
